import { useState, useEffect } from 'react'
import { Subscriber, SubscriberStatus, VehicleType } from '@/lib/types'
import { useApp } from '@/store/AppContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatPlate } from '@/lib/pricing'
import { useToast } from '@/hooks/use-toast'

interface Props {
  open: boolean
  onClose: () => void
  subscriber?: Subscriber | null
}

export function SubscriberForm({ open, onClose, subscriber }: Props) {
  const { subscribers, addSubscriber, updateSubscriber } = useApp()
  const { toast } = useToast()

  const [name, setName] = useState('')
  const [document, setDocument] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [platesStr, setPlatesStr] = useState('')
  const [vehicleType, setVehicleType] = useState<VehicleType>('Carro')
  const [monthlyFee, setMonthlyFee] = useState(150)
  const [dueDate, setDueDate] = useState(5)
  const [status, setStatus] = useState<SubscriberStatus>('Ativo')

  useEffect(() => {
    if (subscriber && open) {
      setName(subscriber.name)
      setDocument(subscriber.document)
      setPhone(subscriber.phone)
      setEmail(subscriber.email)
      setPlatesStr(subscriber.plates.join(', '))
      setVehicleType(subscriber.vehicleType)
      setMonthlyFee(subscriber.monthlyFee)
      setDueDate(subscriber.dueDate)
      setStatus(subscriber.status)
    } else if (open) {
      setName('')
      setDocument('')
      setPhone('')
      setEmail('')
      setPlatesStr('')
      setVehicleType('Carro')
      setMonthlyFee(150)
      setDueDate(5)
      setStatus('Ativo')
    }
  }, [subscriber, open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const plates = platesStr
      .split(',')
      .map((p) => formatPlate(p.trim()))
      .filter((p) => p.length >= 7)
    if (plates.length === 0) {
      return toast({
        title: 'Erro',
        description: 'Informe pelo menos uma placa válida.',
        variant: 'destructive',
      })
    }

    const existing = subscribers.find(
      (s) =>
        s.id !== subscriber?.id && s.plates.some((p) => plates.includes(p)),
    )
    if (existing) {
      return toast({
        title: 'Erro',
        description: `Placa já cadastrada para o cliente: ${existing.name}`,
        variant: 'destructive',
      })
    }

    const data = {
      name,
      document,
      phone,
      email,
      plates,
      vehicleType,
      monthlyFee,
      dueDate,
      status,
    }

    if (subscriber) {
      updateSubscriber(subscriber.id, data)
      toast({ title: 'Sucesso', description: 'Mensalista atualizado.' })
    } else {
      addSubscriber(data)
      toast({ title: 'Sucesso', description: 'Mensalista cadastrado.' })
    }
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {subscriber ? 'Editar Mensalista' : 'Novo Mensalista'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>
                Nome Completo <span className="text-destructive">*</span>
              </Label>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>CPF/CNPJ</Label>
              <Input
                value={document}
                onChange={(e) => setDocument(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>
                Placas (separadas por vírgula){' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                required
                placeholder="ABC-1234, XYZ-9876"
                value={platesStr}
                onChange={(e) => setPlatesStr(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>
                Tipo de Veículo <span className="text-destructive">*</span>
              </Label>
              <Select
                value={vehicleType}
                onValueChange={(v: VehicleType) => setVehicleType(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Carro">Carro</SelectItem>
                  <SelectItem value="Moto">Moto</SelectItem>
                  <SelectItem value="Outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Status <span className="text-destructive">*</span>
              </Label>
              <Select
                value={status}
                onValueChange={(v: SubscriberStatus) => setStatus(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Inadimplente">Inadimplente</SelectItem>
                  <SelectItem value="Suspenso">Suspenso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Valor Mensal (R$) <span className="text-destructive">*</span>
              </Label>
              <Input
                required
                type="number"
                step="0.5"
                value={monthlyFee}
                onChange={(e) => setMonthlyFee(Number(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>
                Dia Vencimento <span className="text-destructive">*</span>
              </Label>
              <Input
                required
                type="number"
                min={1}
                max={31}
                value={dueDate}
                onChange={(e) => setDueDate(Number(e.target.value))}
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full mt-4 font-bold h-12 shadow-sm"
          >
            Salvar Mensalista
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
